import axios from 'axios';
import api from '@/lib/axios';
import { useAuthStore } from '@/stores/auth-store';

export interface BootStepDef {
  id: string;
  label: string;
  description: string;
}

export const BOOT_STEPS: BootStepDef[] = [
  { id: 'server', label: 'Servidor', description: 'Conectando al servidor' },
  { id: 'session', label: 'Sesión', description: 'Verificando sesión' },
  { id: 'config', label: 'Configuración', description: 'Cargando configuración' },
  { id: 'ready', label: 'Listo', description: 'Preparando todo' },
];

export type StepStatus = 'pending' | 'running' | 'done' | 'error';

export interface BootStepState {
  id: string;
  label: string;
  description: string;
  status: StepStatus;
}

export interface BootResult {
  success: boolean;
  error?: string;
  failedStep?: string;
  isAuthenticated: boolean;
}

type OnStepUpdate = (stepId: string, status: StepStatus) => void;
type OnProgress = (percent: number) => void;

const MIN_DURATION = 2000; // Minimum splash duration (2s)

export async function runBootSequence(
  onStepUpdate: OnStepUpdate,
  onProgress: OnProgress,
): Promise<BootResult> {
  const startTime = Date.now();

  // ── Step 1: Health Check ──
  onStepUpdate('server', 'running');
  onProgress(5);
  let isOfflineMode = false;
  try {
    const resp = await api.get('/health', { timeout: 3000 });
    if (resp.data?.status !== 'ok') {
      throw new Error('El servidor respondió con un estado inesperado');
    }
    onStepUpdate('server', 'done');
    onProgress(25);
  } catch (err: any) {
    const isNetworkError = !err.response && (err.code === 'ERR_NETWORK' || err.message === 'Network Error');
    if (isNetworkError || err.code === 'ECONNABORTED') {
      console.warn('Boot Service: No hay conexión con el backend central. Entrando en modo offline (si hay sesión).');
      isOfflineMode = true;
      onStepUpdate('server', 'done'); // Lo pasamos a done para no bloquear
      onProgress(25);
    } else {
      const msg = 'Error de conexión con el servidor.';
      onStepUpdate('server', 'error');
      return { success: false, error: msg, failedStep: 'server', isAuthenticated: false };
    }
  }

  // ── Step 2: Session Validation ──
  onStepUpdate('session', 'running');
  onProgress(35);
  const token = localStorage.getItem('accessToken');
  let isAuthenticated = false;

  if (token) {
    if (isOfflineMode) {
      // If we are offline but have a token, trust the persisted state to allow entry
      const store = useAuthStore.getState();
      if (store.isAuthenticated && store.user) {
        isAuthenticated = true;
      } else {
        // We have a token but no persisted user, force logout
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        useAuthStore.getState().logout();
      }
      onStepUpdate('session', 'done');
      onProgress(55);
    } else {
      try {
        const profileResp = await api.get('/auth/profile');
        if (profileResp.data) {
          // Token is valid — update the store if needed
          const store = useAuthStore.getState();
          if (!store.isAuthenticated) {
            store.logout();
          } else {
            isAuthenticated = true;
          }
        }
        onStepUpdate('session', 'done');
        onProgress(55);
      } catch {
        // Token expired or invalid — clear session gracefully
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        useAuthStore.getState().logout();
        isAuthenticated = false;
        onStepUpdate('session', 'done');
        onProgress(55);
      }
    }
  } else {
    // No token — user will need to log in
    isAuthenticated = false;
    onStepUpdate('session', 'done');
    onProgress(55);
  }

  // ── Step 3: Load Config ──
  onStepUpdate('config', 'running');
  onProgress(65);
  try {
    if (isAuthenticated) {
      // Pre-validate that company data is accessible
      const store = useAuthStore.getState();
      if (store.user?.company?.id) {
        // Quick verification that the company exists
        // Could also preload catalog data here if needed in the future
      }
    }
    // Brief pause so the step is visible (looks intentional)
    await new Promise((r) => setTimeout(r, 200));
    onStepUpdate('config', 'done');
    onProgress(80);
  } catch {
    onStepUpdate('config', 'done');
    onProgress(80);
  }

  // ── Final: Almost Ready ──
  onStepUpdate('ready', 'running');
  onProgress(90);

  // Ensure minimum duration so the splash doesn't flash by
  const elapsed = Date.now() - startTime;
  if (elapsed < MIN_DURATION) {
    await new Promise((r) => setTimeout(r, MIN_DURATION - elapsed));
  }

  onStepUpdate('ready', 'done');
  onProgress(100);

  return { success: true, isAuthenticated };
}
